import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useParams, useRouteMatch } from 'react-router';
import { Redirect } from 'react-router-dom';
import MatrixMenu from 'components/menus/MatrixMenu';
import MatrixSettings from 'components/matrices/MatrixSettings/MatrixSettings';
import MatrixChartContainer from 'components/MatrixChartContainer';
import MatrixSidebar from 'components/MatrixSidebar';
import BuildMatrixModal from 'components/guide/BuildMatrixModal';
import Spinner from 'components/Spinner';
import UncertaintyModal from 'components/matrices/UncertaintyModal';
import { fetchMatrix, selectMatrix } from 'actions/matrices';
import {
  getMatrix,
  shouldFetchMatrix,
  getExperiment,
} from 'constants/matrices';
import DetailsPage from 'components/DetailsPage';
import './MatrixView.scss';
import ConnectionsSidebar from 'components/sidebars/ConnectionsSidebar';
import * as urls from 'constants/urls';
import { usePlayModeState } from '../state/PlayModeState';
import { useViewState } from '../state/ViewState';
import { setNodeOverrides as setPlayModeNodeOverrides } from 'actions/matrixSettings';
import { useCanEdit, useIsInUse } from 'hooks/matrices';
import { writePermissions } from 'constants/auth';

const MatrixView = () => {
  const dispatch = useDispatch();

  const resetPlayModeState = usePlayModeState(state => state.resetState);

  const { connectionsTabOpen, setConnectionsTabOpen } = useViewState(
    state => state
  );

  // TODO I'd like to just make this matrixId, but not sure offhand where route params are set
  const { matrix: matrixId, experimentId } = useParams();

  useEffect(() => {
    dispatch(selectMatrix(matrixId));
  }, [dispatch, matrixId]);

  const matricesLoading = useSelector(state => state.matrices._loading);
  const matricesLoaded = useSelector(state => state.matrices._loaded);
  const matrices = useSelector(state => state.matrices.matrices);

  const matrix = useMemo(() => getMatrix(matrices, matrixId), [
    matrixId,
    matrices,
  ]);
  const experiment = useMemo(
    () => getExperiment(matrices, matrix, experimentId),
    [matrices, matrix, experimentId]
  );

  const { user } = useSelector(state => state.auth);
  const { user_id } = user?.claims ?? { user_id: null };
  const canEdit = useCanEdit();

  const hasEditAccess = useMemo(
    () => writePermissions.includes((experiment ?? matrix)?.roles?.[user_id]),
    [matrix, user_id, experiment]
  );
  const isInUse = useIsInUse({ id: (experiment ?? matrix)?.id, hasEditAccess });

  const matrixIsInUse = useMemo(() => {
    return (
      hasEditAccess &&
      !!isInUse &&
      isInUse?.user !== user_id &&
      isInUse?.user !== null
    );
  }, [user_id, isInUse, hasEditAccess]);

  const editable = useMemo(() => {
    // TODO This will require more nuance because they need to edit the matrix to choose this
    const isViewOnly = (experiment ?? matrix)?.isViewOnly;
    return canEdit(experiment ?? matrix) && !matrixIsInUse && !isViewOnly;
  }, [matrixIsInUse, matrix, canEdit, experiment]);

  const cancelUrl = useMemo(() => {
    if (!matrix) return;
    return matrix?.parentId ? urls.experiment(matrix) : urls.matrix(matrix);
  }, [matrix]);

  const viewMode = useMemo(() => (editable ? 'edit' : 'play'), [editable]);

  // Reset the connections table
  useEffect(() => {
    if (viewMode !== 'edit') {
      setConnectionsTabOpen(false);
    }
  }, [viewMode]);

  useEffect(() => {
    if (shouldFetchMatrix(matrixId, matrix, matricesLoaded, matricesLoading)) {
      dispatch(fetchMatrix(matrixId));
    }

    if (
      shouldFetchMatrix(
        experimentId,
        experiment,
        matricesLoaded,
        matricesLoading
      )
    ) {
      dispatch(fetchMatrix(experimentId));
    }
  }, [
    dispatch,
    experiment,
    experimentId,
    matrix,
    matrixId,
    matricesLoaded,
    matricesLoading,
    matrices,
  ]);

  let { path } = useRouteMatch();

  const isLoaded = useMemo(() => {
    let loaded = true;
    if (matrixId) {
      loaded = matricesLoaded[matrixId];
    }
    if (experimentId && loaded) {
      loaded = matricesLoaded[experimentId];
    }
    return loaded;
  }, [matrixId, experimentId, matricesLoaded]);

  const isNotFound = isLoaded && !matrix;

  useEffect(() => {
    return () => {
      dispatch(setPlayModeNodeOverrides([]));
      resetPlayModeState();
    };
  }, []);

  // This is always 'nodes' or 'graphs'
  const [chartView, setChartView] = useState(false);

  // Reset on leaving
  useEffect(() => {
    return () => setConnectionsTabOpen(false);
  }, []);

  // ------------------------------------------------------------------------------

  const [publicWarningDismissed, setPublicWarningDismissed] = useState(false);
  const [showPublicWarning, setShowPublicWarning] = useState(false);

  const onCancelPublicWarning = useCallback(v => {
    setPublicWarningDismissed(v);
    setShowPublicWarning(false);
  }, []);

  return (
    <>
      <Switch>
        <Route>
          <div className="MatrixView">
            {isNotFound ? (
              <div className="MatrixView-spinner-container">
                Matrix not found.
              </div>
            ) : isLoaded ? (
              <div className="MatrixView-Content interactiveTourGettingStarted-3">
                <MatrixMenu matrix={experiment ?? matrix} viewMode={viewMode} />

                <div className="MatrixView-Container">
                  <div className={'MatrixView-Main'}>
                    <div className="MatrixView-Overflow">
                      <Switch>
                        <Route exact path={`${path}/new`}>
                          <MatrixChartContainer
                            matrix={experiment ?? matrix}
                            base={experiment ? matrix : null}
                            viewMode={viewMode}
                            connectionsTabOpen={connectionsTabOpen}
                            setConnectionsTabOpen={setConnectionsTabOpen}
                            chartView={chartView}
                            setChartView={setChartView}
                          />
                          <ConnectionsSidebar
                            matrix={experiment ?? matrix}
                            setChartView={setChartView}
                            chartView={chartView}
                            setConnectionsTabOpen={setConnectionsTabOpen}
                          />
                          {matrix ? <BuildMatrixModal matrix={matrix} /> : null}
                        </Route>
                        {/* Primary view */}
                        {/* Default view (at bottom to avoid exact path) */}
                        <Route path={`${path}`}>
                          <MatrixChartContainer
                            matrix={experiment ?? matrix}
                            base={experiment ? matrix : null}
                            viewMode={viewMode}
                            connectionsTabOpen={connectionsTabOpen}
                            setConnectionsTabOpen={setConnectionsTabOpen}
                            chartView={chartView}
                            setChartView={setChartView}
                            publicWarningDismissed={publicWarningDismissed}
                            setPublicWarningDismissed={
                              setPublicWarningDismissed
                            }
                            showPublicWarning={showPublicWarning}
                            setShowPublicWarning={setShowPublicWarning}
                            onCancelPublicWarning={onCancelPublicWarning}
                          />
                          <ConnectionsSidebar
                            matrix={experiment ?? matrix}
                            setChartView={setChartView}
                            chartView={chartView}
                            setConnectionsTabOpen={setConnectionsTabOpen}
                          />
                        </Route>
                      </Switch>

                      {/* Modals */}
                      <Switch>
                        {/* Uncertainty */}
                        <Route exact path={`${path}/uncertainty`}>
                          <UncertaintyModal
                            matrix={experiment ?? matrix}
                            runOnMount={() => {
                              if (!connectionsTabOpen) {
                              }
                            }}
                          />
                        </Route>

                        {/* Edit matrix settings */}
                        <Route exact path={`${path}/edit`}>
                          <MatrixSettings
                            matrix={experiment ?? matrix}
                            base={experiment ? matrix : null}
                            saveUrl={
                              experiment
                                ? urls.experiment(experiment)
                                : urls.matrix(matrix)
                            }
                            cancelUrl={
                              experiment
                                ? urls.experiment(experiment)
                                : urls.matrix(matrix)
                            }
                            publicWarningDismissed={publicWarningDismissed}
                            showPublicWarning={showPublicWarning}
                            setShowPublicWarning={setShowPublicWarning}
                          />
                        </Route>
                      </Switch>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="MatrixView-spinner-container">
                <Spinner />
              </div>
            )}
            {matrix && matrix.nodes && viewMode === 'edit' ? (
              <MatrixSidebar
                base={experiment ? matrix : null}
                matrix={experiment ?? matrix}
              />
            ) : null}
          </div>
        </Route>
      </Switch>
    </>
  );
};

export default MatrixView;
